import React from 'react';
import { Fab, Paper, Stack as MuiStack } from '@mui/material';
import { DataTypeProvider, FilteringState, SortingState } from '@devexpress/dx-react-grid';
import { Grid, TableColumnResizing, TableFilterRow, TableHeaderRow, VirtualTable } from '@devexpress/dx-react-grid-material-ui';
import { applyGridTableFilter } from './_Utils/AppDocuCommon';
import AddDocumentDialog from './Document_Components/AddDocumentDialog';
import ActionsTypeProvider from './Document_TypeProviders/ActionsTypeProvider';
import RowNumTypeProvider from './_Utils/RowNumTypeProvider';
import { tableColumns, tableColumnWidths, tableColumnExtensions, tableFilteringStateColumnExtensions, tableSortingStateColumnExtensions } from './Document_Components/_GridTableProps';
import AddIcon from '@mui/icons-material/Add';
import FileSizeTypeProvider from './Document_TypeProviders/FileSizeTypeProvider';
import NameTypeProvider from './Document_TypeProviders/NameTypeProvider';
import { DateTypeProvider } from './_Utils/DateTypeProvider';
import DescriptionTypeProvider from './Document_TypeProviders/DescriptionTypeProvider';
import DocumentTypeTypeProvider from './Document_TypeProviders/DocumentTypeTypeProvider';
import LanguageTypeProvider from './Document_TypeProviders/LanguageTypeProvider';
import ComBrandTypeProvider from './_Utils/ComBrandTypeProvider';

class AppDocuDocumentPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      tableColumnWidths: tableColumnWidths,
      tableFilters: [],
      localItems: this.getFilteredItems(props.items, []),
      addDialogIsOpen: false,
      tableSorting: [], // stato per ordinamento
    };
  }
  getFilteredItems(items, filters) {
    let filteredItems = items || [];
    filteredItems = filteredItems.filter(row => {
      return filters.every(f => {
        const col = tableColumns.find(c => c.name === f.columnName);
        const isNumber = col && col.isNumber;
        // Special case: filter on 'name' column should match both 'name' and 'original_filename'
        if (f.columnName === 'name') {
          // Only apply if filter value is not empty
          if (!f.value) return true;
          const value = String(f.value).toLowerCase();
          const nameMatch = row.name && String(row.name).toLowerCase().includes(value);
          const origFileMatch = row.original_filename && String(row.original_filename).toLowerCase().includes(value);
          return nameMatch || origFileMatch;
        }
        return applyGridTableFilter(row[f.columnName], f.value, f.operation || "contains", isNumber);
      });
    });
    filteredItems = filteredItems.map((m, idx) => ({
      ...m,
      row_num: idx + 1,      
    }));
    return filteredItems;
  }
  setTableFilters(value) {
    this.setState({
      tableFilters: value,
      localItems: this.getFilteredItems(this.props.items, value)
    });
  }
  setTableSorting(value) {
    this.setState({
      tableSorting: value,
      localItems: this.getSortedItems(this.getFilteredItems(this.props.items, this.state.tableFilters), value)
    });
  }
  getSortedItems(items, sorting) {
    if (!sorting || sorting.length === 0) return items;
    const sorted = [...items];
    sorting.slice().reverse().forEach(sort => {
      sorted.sort((a, b) => {
        let aValue = a[sort.columnName];
        let bValue = b[sort.columnName];
        if (sort.columnName === 'name') {
          aValue = (aValue || '').toLowerCase();
          bValue = (bValue || '').toLowerCase();
          if (aValue < bValue) return sort.direction === 'asc' ? -1 : 1;
          if (aValue > bValue) return sort.direction === 'asc' ? 1 : -1;
        } else if (sort.columnName === 'created_at' || sort.columnName === 'updated_at') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
          if (aValue > bValue) return sort.direction === 'asc' ? -1 : 1;
          if (aValue < bValue) return sort.direction === 'asc' ? 1 : -1;
        } else {
          if (aValue < bValue) return sort.direction === 'asc' ? -1 : 1;
          if (aValue > bValue) return sort.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    });
    return sorted;
  }
  componentDidUpdate(prevProps) {
    if (prevProps.items !== this.props.items) {
      const filtered = this.getFilteredItems(this.props.items, this.state.tableFilters);
      const sorted = this.getSortedItems(filtered, this.state.tableSorting);
      this.setState({
        localItems: sorted
      });
    }
  }  
  render() {    
    var height = "calc(100vh - " + this.props.navbarHeight + ")"
    return (
      <Paper>
        <MuiStack direction="row" spacing={2} sx={{ position: 'fixed', bottom: (theme) => theme.spacing(3), right: (theme) => theme.spacing(5), zIndex: 1200 }}>
          <Fab
            onClick={() => this.setState({ addDialogIsOpen: true })}
            variant="extended"
            color="primary"
            aria-label="add" >
            <AddIcon sx={{ mr: 1 }} />
            ADD NEW
          </Fab>
        </MuiStack>
        
        <Grid 
          rows={this.state.localItems}
          columns={tableColumns}
          getRowId={row => row.id} 
        >
          <ActionsTypeProvider 
            for={[ "actions" ]} 
            onItemsChange={this.props.onItemsChange} />
          <DateTypeProvider
            for={["created_at", "updated_at"]}
            />
          <RowNumTypeProvider for={[ "row_num" ]} />
          <DocumentTypeTypeProvider for={[ "document_type" ]} />
          <LanguageTypeProvider for={[ "language" ]} />
          <DescriptionTypeProvider for={[ "description" ]} />
          <FileSizeTypeProvider for={[ "file_size" ]} />
          <NameTypeProvider for={[ "name" ]} />
          <ComBrandTypeProvider for={[ "com_brand" ]} />          
          <DataTypeProvider  
            for={[ "id", "file_size" ]}
            availableFilterOperations={[ 'equal', 'notEqual', 'greaterThan', 'greaterThanOrEqual', 'lessThan', 'lessThanOrEqual' ]} />

          <VirtualTable 
            height={height}
            columnExtensions={tableColumnExtensions} />
          <FilteringState
            filters={this.state.tableFilters}
            onFiltersChange={this.setTableFilters.bind(this)}
            columnExtensions={tableFilteringStateColumnExtensions} />
          <SortingState
            sorting={this.state.tableSorting}
            onSortingChange={this.setTableSorting.bind(this)}
            columnExtensions={tableSortingStateColumnExtensions} />
          <TableColumnResizing
            columnWidths={this.state.tableColumnWidths}
            onColumnWidthsChange={value => this.setState({ tableColumnWidths: value })} />
          <TableHeaderRow 
            contentComponent={props => (<TableHeaderRow.Content {...props} style={{ minHeight: 0, height: 0, paddingTop: 0, paddingBottom: 0, fontSize: 12 }} />)}
            showSortingControls
          />
          <TableFilterRow showFilterSelector />
        </Grid>

        <AddDocumentDialog
          isOpen={this.state.addDialogIsOpen}
          onClose={() => this.setState({ addDialogIsOpen: false })}
          onItemsChange={this.props.onItemsChange} />
      </Paper>
    );
  }
}

export default AppDocuDocumentPage;
